import { Injectable } from '@angular/core';
import { BehaviorSubject, of, Observable, bindNodeCallback, from } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { CognitoUserPool, CognitoUserSession  } from 'amazon-cognito-identity-js';
import { CognitoIdentityCredentials, config } from 'aws-sdk';
import { Router } from '@angular/router';
import { User } from '../../model';
import Auth from '@aws-amplify/auth';
import * as _ from 'lodash';
import { IdleTimeoutService } from '../idle-timeout/idle-timeout.service';
import { Settings } from 'src/app/utils/constant/settings';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private _whenLoggedIn = new BehaviorSubject<User>(null);
  private _userPool: CognitoUserPool;
  private _user: User;
  private _hostToken = new BehaviorSubject<User>(null);

  get whenLoggedIn() { return this._whenLoggedIn };
  get userPool() { return this._userPool }
  get hostToken() { return this._hostToken };
  get user() { return this._user }
  set user(user: User) { this._user = user };

  constructor(
    private router: Router
  ) {
    this._userPool = new CognitoUserPool({
      UserPoolId: environment.auth.userPoolId,
      ClientId: environment.auth.userPoolWebClientId
    });
    this.initAWSCredentials();
  }

  private setUser(user: User) {
    this._user = user
    this._whenLoggedIn.next(user);
  }

  setToken(value) {
    this._hostToken.next(value);
  }

  public async initAWSCredentials() {
    let cognitoUser = this.userPool.getCurrentUser();
    
    if (!cognitoUser) {
      return;
    }

    this.setUser(new User(cognitoUser));

    cognitoUser.getSession(async (err, session: CognitoUserSession) => {
      if (err) {
        return console.log(err);
      }
      let logins = {
        [environment.cognitoIdentityEndpoint]: session.getIdToken().getJwtToken()
      };

      let creds = new CognitoIdentityCredentials({
        IdentityPoolId: environment.auth.identityPoolId,
        Logins: logins
      }, {
          region: environment.region
        });

      config.credentials = creds;

      await ((config.credentials) as CognitoIdentityCredentials).refreshPromise();
    });
  }

  public checkAWSCredentials(callback: (err?) => void) {
    let cognitoUser = this.userPool.getCurrentUser();

    if (!cognitoUser) {
      return callback(true);
    }

    if (environment.showLogs) {
      console.log("Needs to refresh token: ", ((config.credentials) as CognitoIdentityCredentials).needsRefresh());
    }

    if (((config.credentials) as CognitoIdentityCredentials).needsRefresh()) {

      cognitoUser.getSession((err, session: CognitoUserSession) => {
        if (err) {
          console.log(err);
          return callback(err);
        }

        cognitoUser.refreshSession(session.getRefreshToken(), (err, session: CognitoUserSession) => {
          if (err) {
            console.log(err);
            return callback(err);
          }

          let logins = {
            [environment.cognitoIdentityEndpoint]: session.getIdToken().getJwtToken()
          };

          let creds = new CognitoIdentityCredentials({
            IdentityPoolId: environment.auth.identityPoolId,
            Logins: logins
          }, {
              region: environment.region
            });

          config.credentials = creds;

          ((config.credentials) as CognitoIdentityCredentials).refresh((err) => {
            if (err) {
              console.log(err);
              return callback(err);
            }
            if (environment.showLogs) {
              console.log("Token refreshed. Logins: ", logins);
            }
            callback();
          });
        });

      });
    } else {
      if (environment.showLogs) {
        console.log("No need to refresh token.");
      }
      callback();
    }
  }

  public logout() {
    localStorage.clear();
    sessionStorage.clear();
    
    let cognitoUser = this.userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
 
    // Clear Cognito Cache ID and re-initialize
    if(config.credentials) {
      ((config.credentials) as CognitoIdentityCredentials).clearCachedId();
      let creds = new CognitoIdentityCredentials({
        IdentityPoolId: environment.auth.identityPoolId
      }, {
          region: environment.region
        });
      config.credentials = creds;
    }
    this.setUser(null);
    this.setToken(null);
    this.router.navigate(['/login']);
  }

  public logoutSilent() {
    localStorage.clear();
    sessionStorage.clear();
    
    let cognitoUser = this.userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
 
    // Clear Cognito Cache ID and re-initialize
    if(config.credentials) {
      ((config.credentials) as CognitoIdentityCredentials).clearCachedId();
      let creds = new CognitoIdentityCredentials({
        IdentityPoolId: environment.auth.identityPoolId
      }, {
          region: environment.region
        });
      config.credentials = creds;
    }
    this.setUser(null);
    this.setToken(null);
  }

  public hasPermission(permissions: string | string[]) {
    if (_.isEmpty(permissions) || (_.isArray(permissions) && permissions.length == 0)) {
      return true;
    }

    if (_.isArray(permissions)) {
      return _.isEmpty(_.difference(permissions, _.split(localStorage.getItem('permission'), ',')));
    } else {
      return _.indexOf(_.split(localStorage.getItem('permission'), ','), permissions) >= 0;
    }
  }

  public getUserSession() {
    const user = this.getUserPool().getCurrentUser();
    const getUserSession = bindNodeCallback((callback:any) => user.getSession(callback));
    return getUserSession();
  }

  getUserPool() {
    let userPool;
    return userPool = new CognitoUserPool({
      UserPoolId : environment.auth.userPoolId,
      ClientId : environment.auth.userPoolWebClientId
    });
  }

  public clearCredentials() {
    localStorage.clear();
    sessionStorage.clear();
    
    let cognitoUser = this.userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
 
    // Clear Cognito Cache ID and re-initialize
    if(config.credentials) {
      ((config.credentials) as CognitoIdentityCredentials).clearCachedId();
      let creds = new CognitoIdentityCredentials({
        IdentityPoolId: environment.auth.identityPoolId
      }, {
          region: environment.region
        });
      config.credentials = creds;
    }
    this.setUser(null);
    this.setToken(null);
  }

  getCredentials() {
    return from(Auth.currentCredentials())
  }

  getEssentialCredentials() {
    return this.getCredentials().pipe(
      map((creds) => Auth.essentialCredentials(creds))
    )
  } 

  isSessionExpired(): boolean {
    const storedSettings = localStorage.getItem('settings');
    const settings: Settings = storedSettings
      ? JSON.parse(storedSettings)
      : { IDLE_TIMEOUT_IN_SECONDS: 300 };

    // Maintain the remaining time until timeout even when the user has closed the page
    // Only reset the timer if the page was accessed again no longer than the idle timeout setting
    // Otherwise, log the user out
    const lastActivityTimestamp = localStorage.getItem('lastActivityTimestamp');
    if (!lastActivityTimestamp) {
      return true; // no timestamp means expired
    }

    const idleTimeoutDateTime = new Date(
      new Date(lastActivityTimestamp).getTime() + settings.IDLE_TIMEOUT_IN_SECONDS * 1000
    );

    return Date.now() > idleTimeoutDateTime.getTime();
  }
}